import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Printer, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const fmt = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString([], { dateStyle: "long" }); } catch { return iso; } };

// Clean printable Family Transition report (roadmap + checklist + recaps). PDF export deferred.
export default function ClientReport() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/portal/roadmap").then((r) => r.data.roadmap).catch(() => null),
      api.get("/portal/checklist").then((r) => r.data.items).catch(() => []),
      api.get("/portal/recaps").then((r) => r.data.items).catch(() => []),
      api.get("/portal/welcome").then((r) => r.data).catch(() => ({})),
    ]).then(([roadmap, checklist, recaps, welcome]) => setData({ roadmap, checklist, recaps, welcome }));
  }, []);

  if (!data) return <div className="max-w-2xl mx-auto p-6 space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;
  const { roadmap, checklist, recaps, welcome } = data;

  return (
    <div className="bg-white min-h-screen" data-testid="client-report-page">
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff; } }`}</style>
      <div className="no-print sticky top-0 bg-[#1C4358] text-white px-4 py-3 flex items-center justify-between">
        <button onClick={() => nav("/portal")} className="inline-flex items-center gap-1.5 text-[14px]"><ArrowLeft size={16} /> Back to portal</button>
        <Button onClick={() => window.print()} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 rounded-full" data-testid="report-print-btn"><Printer size={16} /> Print / Save as PDF</Button>
      </div>

      <div className="max-w-3xl mx-auto p-8 print:p-0">
        <div className="border-b-2 border-[#1C4358] pb-4 mb-6">
          <div className="text-[#9C1E22] font-bold text-lg">MovingMyMama</div>
          <h1 className="font-display text-3xl font-bold text-[#1C4358] mt-1">Family Transition Report</h1>
          <div className="text-[14px] text-[#6B7280] mt-1">Prepared for {welcome?.name || "your family"} · {fmt(new Date().toISOString())}</div>
        </div>

        {roadmap ? (
          <section className="mb-8" data-testid="report-roadmap">
            <h2 className="text-[20px] font-bold text-[#1C4358] mb-2">{roadmap.title}</h2>
            {roadmap.situation_summary && <p className="text-[15px] text-[#252221] leading-relaxed">{roadmap.situation_summary}</p>}
            {roadmap.main_concern && <p className="text-[15px] text-[#252221] mt-2"><b>What matters most:</b> {roadmap.main_concern}</p>}
            {(roadmap.next_steps || []).length > 0 && (
              <div className="mt-3"><div className="font-semibold text-[#1C4358]">Recommended next steps</div>
                <ol className="list-decimal ml-5 mt-1 space-y-1 text-[15px] text-[#252221]">{roadmap.next_steps.map((s, i) => <li key={i}><b>{s.title}</b>{s.description ? ` — ${s.description}` : ""}</li>)}</ol></div>
            )}
            {(roadmap.documents_needed || []).length > 0 && (
              <div className="mt-3"><div className="font-semibold text-[#1C4358]">Documents that will help</div>
                <ul className="list-disc ml-5 mt-1 text-[15px] text-[#252221]">{roadmap.documents_needed.map((d, i) => <li key={i}>{d}</li>)}</ul></div>
            )}
          </section>
        ) : <p className="text-[15px] text-[#6B7280] mb-8">Your roadmap will appear here once your guide publishes it.</p>}

        {(checklist || []).length > 0 && (
          <section className="mb-8" data-testid="report-checklist">
            <h2 className="text-[20px] font-bold text-[#1C4358] mb-2">Your family checklist</h2>
            <ul className="space-y-1.5 text-[15px] text-[#252221]">
              {checklist.map((c) => <li key={c.id}>{c.status === "completed" ? "☑" : "☐"} {c.title}</li>)}
            </ul>
          </section>
        )}

        {(recaps || []).length > 0 && (
          <section className="mb-8" data-testid="report-recaps">
            <h2 className="text-[20px] font-bold text-[#1C4358] mb-2">Meeting recaps</h2>
            {recaps.map((r) => (
              <div key={r.meeting_id} className="mb-4">
                <div className="font-semibold text-[#252221]">{r.title} · {fmt(r.scheduled_at)}</div>
                {r.summary && <p className="text-[15px] text-[#252221] mt-1">{r.summary}</p>}
              </div>
            ))}
          </section>
        )}

        <div className="border-t border-[#E6EDF2] pt-4 text-[12px] text-[#9AA5B1] leading-relaxed">
          This report reflects your conversations with MovingMyMama and is general guidance only — it is not legal, medical, financial, real-estate, or care-placement advice or a guarantee of any outcome. MovingMyMama · Your Trusted Resource For Senior Transitions.
        </div>
      </div>
    </div>
  );
}
